import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  Loader2,
  Search,
  Users,
  Eye,
  Mail,
  CheckCircle2,
} from "lucide-react";
import {
  getAPI,
  patchAPI,
  postPagedSearchAbsolute,
} from "../../api/frameworksApi";
import { TableToolbar } from "../components/TableToolbar";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction } from "../components/RBACButton";
import { Tip } from "../components/ui/tooltip";
import { API_URL } from "../../constants";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { StatusBadge } from "../components/StatusBadge";
import { Dropdown } from "../components/Dropdown";
import { RowsPerCardSelect } from "../components/EntityCardGrid";
import { DataTable, cycleSortDir, type Column } from "../components/DataTable";

/* ── Types ─────────────────────────────────────────────── */

interface UserRole {
  id: string;
  roleName: string;
  displayName: string;
  scopeOrganization: {
    id: string;
    name: string;
    shortName: string;
  } | null;
  isActive: boolean;
  grantedAt: string;
}

interface UserOrg {
  id: string;
  name: string;
  shortName: string;
}

interface UserRecord {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
  displayName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  platformRoles: UserRole[];
  organizations: UserOrg[];
  createdAt: string;
}

interface OrgUnit {
  id: string;
  name: string;
  shortName: string;
  orgType?: string;
}

interface MappingForProvenance {
  mappingId: string;
  groupName: string;
  groupDn: string;
  orgId: string;
  orgName: string;
}

interface RoleWithMappings {
  id: string;
  name: string;
  groupMappings: MappingForProvenance[];
}

type StatusFilter = "" | "active" | "inactive";
type DrawerTab = "overview" | "roles";

/**
 * Sortable column key -> backend field name sent as `sortByColumn` in
 * POST {users}/{page}/{size}. Sorting is fully server-side; a key with no
 * entry here is forwarded as-is.
 */
const USER_SORT_COLUMNS: Record<string, string> = {
  displayName: "displayName",
  email: "email",
  lastLoginAt: "lastLoginAt",
};

/* ── Component ─────────────────────────────────────────── */

export function UserManagement() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters seeded from URL once on mount, owned by component thereafter
  const [userSearch, setUserSearch] = useState(
    () => searchParams.get("q") ?? "",
  );
  const [filterOrgId, setFilterOrgId] = useState(
    () => searchParams.get("org_id") ?? "",
  );
  const [filterRole, setFilterRole] = useState(
    () => searchParams.get("role") ?? "",
  );
  const [filterStatus, setFilterStatus] = useState<StatusFilter>(() => {
    const s = searchParams.get("status");
    return s === "active" || s === "inactive" ? s : "";
  });

  // Pagination — page is 0-indexed, pageSize hydrated from URL
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get("page") ?? "0", 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  // Not hydrated from / synced to the URL — rows-per-page always resets to
  // the default on reload or when navigating back to this page.
  const [pageSize, setPageSize] = useState(10);

  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [userKeyword, setUserKeyword] = useState(
    () => searchParams.get("q") ?? "",
  );
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [filterOrgs, setFilterOrgs] = useState<OrgUnit[]>([]);
  const [allMappings, setAllMappings] = useState<RoleWithMappings[]>([]);

  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
  const [detailUser, setDetailUser] = useState<UserRecord | null>(null);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>("overview");

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the backend's default order),
  // which sends sortByColumn: "" / sortingOrder: "" the way it did on first load.
  const handleSort = (key: string) => {
    const next = cycleSortDir(sortKey, sortDir, key);
    setPage(0); // any order change restarts at the first page
    setSortKey(next.sortKey);
    setSortDir(next.sortDir);
  };

  /* ── Fetch ── */

  // The spinner is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const fetchUsers = useCallback(async () => {
    if (!firstLoadDoneRef.current) setUsersLoading(true);
    try {
      const res = await postPagedSearchAbsolute<UserRecord>(
        API_URL.ADMIN_USERS,
        page,
        pageSize,
        {
          keyword: userKeyword.trim(),
          // Unsorted (the initial state) sends both sort fields as empty.
          sortByColumn: sortKey ? (USER_SORT_COLUMNS[sortKey] ?? sortKey) : "",
          sortingOrder: sortKey ? sortDir : "",
          filters: {
            orgId: filterOrgId || null,
            role: filterRole || null,
            // The Active/Inactive dropdown maps onto the `isActive` flag.
            isActive: filterStatus ? String(filterStatus === "active") : null,
          },
        },
      );
      setUsers(res.items);
      setTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower filter) — step back to the last real page.
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch {
      useToastStore.getState().showError("Error", "Failed to load users");
      setUsers([]);
      setTotal(0);
    } finally {
      firstLoadDoneRef.current = true;
      setUsersLoading(false);
    }
  }, [
    page,
    pageSize,
    userKeyword,
    sortKey,
    sortDir,
    filterOrgId,
    filterRole,
    filterStatus,
  ]);

  // The Role dropdown lists every role in use, not just the ones on the
  // current page, so it keeps its own unpaginated fetch.
  const [allRoleUsers, setAllRoleUsers] = useState<UserRecord[]>([]);
  useEffect(() => {
    getAPI(`${API_URL.ADMIN_USERS}?pageSize=500`)
      .then((res) => setAllRoleUsers(res?.data ?? []))
      .catch(() => {
        /* non-fatal — the role filter just shows fewer options */
      });
  }, []);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitUserSearch = (value: string) => {
    const next = value.trim();
    if (next === userKeyword) return;
    setUserKeyword(next);
    setPage(0);
  };

  const fetchFilterOrgs = useCallback(async () => {
    try {
      const res = await getAPI(API_URL.ORG_UNITS + "?flat=true");
      setFilterOrgs(res?.data ?? []);
    } catch (err: any) {
      setFilterOrgs([]);
      useToastStore
        .getState()
        .showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load organizations",
        );
    }
  }, []);

  const fetchAllMappings = useCallback(async () => {
    try {
      const res = await getAPI(API_URL.PLATFORM_ROLES_WITH_MAPPINGS);
      setAllMappings(res?.data ?? []);
    } catch (err: any) {
      setAllMappings([]);
      useToastStore
        .getState()
        .showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load role mappings",
        );
    }
  }, []);

  // One-time loads (filter dropdowns + mappings for drawer provenance)
  useEffect(() => {
    fetchFilterOrgs();
    fetchAllMappings();
  }, [fetchFilterOrgs, fetchAllMappings]);

  // Every list input — keyword, sort, org/role/status filters, page, page
  // size — is resolved by the backend, so one effect covers them all.
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Sync filters + pagination → URL
  useEffect(() => {
    const next = new URLSearchParams();
    // The committed keyword, not the raw box, so the URL matches the results.
    if (userKeyword.trim()) next.set("q", userKeyword.trim());
    if (filterOrgId) next.set("org_id", filterOrgId);
    if (filterRole) next.set("role", filterRole);
    if (filterStatus) next.set("status", filterStatus);
    if (page > 0) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [
    userSearch,
    filterOrgId,
    filterRole,
    filterStatus,
    page,
    setSearchParams,
  ]);

  // Reset to page 0 whenever a filter narrows the result set
  useEffect(() => {
    setPage(0);
  }, [filterOrgId, filterRole, filterStatus, pageSize]);

  // Role filter options, derived from the roles actually in use across all
  // users (deduped by roleName) — reliable regardless of the platform-roles API.
  const roleOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of allRoleUsers) {
      for (const r of u.platformRoles ?? []) {
        if (r.roleName && !map.has(r.roleName))
          map.set(r.roleName, r.displayName || r.roleName);
      }
    }
    return [...map.entries()]
      .map(([roleName, displayName]) => ({ roleName, displayName }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [allRoleUsers]);

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order, org/role/status filters and page window. */
  const totalItems = total;
  const pagedUsers = users;

  /* ── Handlers ── */

  const handleToggleUserStatus = async (user: UserRecord) => {
    setTogglingUserId(user.id);
    try {
      await patchAPI(`${API_URL.ADMIN_USERS}/${user.id}/status`, {
        active: !user.isActive,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `${user.displayName} ${user.isActive ? "deactivated" : "activated"}`,
        );
      await fetchUsers();
    } catch (err: any) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err?.response?.data?.message ||
            err.message ||
            "Failed to update user status",
        );
    } finally {
      setTogglingUserId(null);
    }
  };

  const openDetail = (u: UserRecord) => {
    setDrawerTab("overview");
    setDetailUser(u);
  };

  const provenanceForGrant = (grant: UserRole) => {
    const role = allMappings.find((r) => r.name === grant.roleName);
    if (!role) return [];
    const orgId = grant.scopeOrganization?.id ?? "";
    return role.groupMappings.filter((m) => m.orgId === orgId);
  };

  const hasActiveFilters =
    !!userSearch.trim() || !!filterOrgId || !!filterRole || !!filterStatus;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Users</h1>
          <p className="text-sm d mt-0.5">
            View users synced from your directory and group mappings
          </p>
        </div>
      </div>

      {/* ── Search & Filters ── */}
      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          className="flex-none w-full min-w-[200px] max-w-sm"
          search={{
            value: userSearch,
            onChange: setUserSearch,
            onSubmit: submitUserSearch,
            placeholder: "Search by name or email",
            ariaLabel: "Search users",
          }}
        />
        <Dropdown
          variant="pill"
          className="min-w-[160px]"
          value={filterOrgId}
          onChange={(v) => setFilterOrgId(v)}
          options={[
            { value: "", label: "All Organizations" },
            ...filterOrgs
              .filter((o) => o.orgType === "ORGANIZATION")
              .map((o) => ({ value: o.id, label: o.name })),
          ]}
        />
        <Dropdown
          variant="pill"
          className="min-w-[140px]"
          value={filterRole}
          onChange={(v) => setFilterRole(v)}
          options={[
            { value: "", label: "All Roles" },
            ...roleOptions.map((r) => ({
              value: r.roleName,
              label: r.displayName,
            })),
          ]}
        />
        <Dropdown
          variant="pill"
          className="min-w-[120px]"
          value={filterStatus}
          onChange={(v) => setFilterStatus(v as StatusFilter)}
          options={[
            { value: "", label: "All Status" },
            { value: "active", label: "Active", dot: "bg-emerald-500" },
            { value: "inactive", label: "Inactive", dot: "bg-slate-400" },
          ]}
        />
        {hasActiveFilters && (
          <button
            onClick={() => {
              setUserSearch("");
              setFilterOrgId("");
              setFilterRole("");
              setFilterStatus("");
            }}
            className="h-[38px] flex items-center rounded-lg border border-border bg-card px-3 text-sm text-muted-foreground transition-colors hover:border-indigo-400/60 hover:text-foreground dark:hover:border-indigo-400/40"
          >
            Clear
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <RowsPerCardSelect
            value={pageSize}
            onChange={(n) => {
              setPageSize(n);
              setPage(0);
            }}
            options={[10, 25, 50, 100]}
            label="Rows per page"
          />
        </div>
      </div>

      {/* ── Users Table ── */}
      {(() => {
        const columns: Column<UserRecord>[] = [
          {
            key: "sno",
            header: "#",
            align: "center",
            render: (_row, idx) => (
              <span className="text-xs font-medium d">
                {page * pageSize + idx + 1}
              </span>
            ),
          },
          {
            key: "displayName",
            header: "User",
            align: "left",
            sortable: true,
            render: (u) => (
              <div className="flex items-center gap-2.5">
                <div
                  className={`size-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    u.isActive
                      ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300"
                      : "bg-[#f1f5f9] dark:bg-white/[0.05] text-[#94a3b8] dark:text-white/30"
                  }`}
                >
                  {u.displayName
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase() || "?"}
                </div>
                <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/85 truncate">
                  {u.displayName}
                </span>
              </div>
            ),
          },
          {
            key: "email",
            header: "Email",
            align: "left",
            sortable: true,
            render: (u) => (
              <span className="text-[13px] text-[#64748b] dark:text-white/50 truncate">
                {u.email}
              </span>
            ),
          },
          {
            key: "roles",
            header: "Roles",
            align: "left",
            render: (u) => {
              const uniqueRoles =
                u.platformRoles?.reduce<
                  { roleName: string; displayName: string }[]
                >((acc, r) => {
                  if (!acc.some((x) => x.roleName === r.roleName))
                    acc.push({
                      roleName: r.roleName,
                      displayName: r.displayName,
                    });
                  return acc;
                }, []) ?? [];
              if (!uniqueRoles.length)
                return (
                  <span className="text-[13px] text-[#94a3b8] dark:text-white/25">
                    —
                  </span>
                );
              return (
                <div className="flex flex-wrap gap-1">
                  {uniqueRoles.slice(0, 2).map((r) => (
                    <span
                      key={r.roleName}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 font-medium border border-violet-200/60 dark:border-violet-500/20 whitespace-nowrap max-w-[140px] truncate"
                    >
                      {r.displayName}
                    </span>
                  ))}
                  {uniqueRoles.length > 2 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f1f5f9] dark:bg-white/[0.04] text-[#64748b] dark:text-white/40 font-medium">
                      +{uniqueRoles.length - 2}
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            key: "lastLoginAt",
            header: "Last Login",
            align: "center",
            sortable: true,
            render: (u) => (
              <span className="text-[13px] text-[#64748b] dark:text-white/50">
                {u.lastLoginAt ? formatRelative(u.lastLoginAt) : "Never"}
              </span>
            ),
          },
          {
            key: "isActive",
            header: "Status",
            align: "center",
            render: (u) => {
              const isToggling = togglingUserId === u.id;
              return (
                <RBACAction module="user" action="update">
                  <Tip label={u.isActive ? "Deactivate User" : "Activate User"}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleUserStatus(u);
                      }}
                      disabled={isToggling}
                      className="relative inline-flex items-center flex-shrink-0 w-10 h-6 rounded-full transition-colors disabled:opacity-50 cursor-pointer"
                      style={{
                        backgroundColor: u.isActive
                          ? "rgb(16 185 129)"
                          : "rgb(100 116 139)",
                      }}
                    >
                      {isToggling ? (
                        <Loader2 className="size-3.5 animate-spin absolute top-[5px] left-1/2 -translate-x-1/2 text-white" />
                      ) : (
                        <span
                          className={`absolute top-[3px] left-[3px] size-[18px] rounded-full bg-white shadow-sm transition-transform duration-200 ${u.isActive ? "translate-x-[16px]" : "translate-x-0"}`}
                        />
                      )}
                    </button>
                  </Tip>
                </RBACAction>
              );
            },
          },
          {
            key: "actions",
            header: "View",
            align: "center",
            render: (u) => (
              <Tip label="View User Details">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetail(u);
                  }}
                  className="p-1.5 rounded-md text-[#94a3b8] dark:text-white/30 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                >
                  <Eye className="size-3.5" />
                </button>
              </Tip>
            ),
          },
        ];

        return (
          <DataTable
            columns={columns}
            data={pagedUsers}
            loading={usersLoading}
            rowKey={(u) => u.id}
            emptyText={
              hasActiveFilters
                ? "No users match your filters"
                : "No users synced yet"
            }
            emptyIcon={
              <Users className="size-4 text-[#64748b] dark:text-white/40" />
            }
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={(p) => setPage(p)}
            itemLabel="users"
            sortKey={sortKey ?? undefined}
            sortDir={sortDir}
            onSort={handleSort}
          />
        );
      })()}

      {/* ── User Detail Drawer ── */}
      <Sheet
        open={!!detailUser}
        onOpenChange={(open) => {
          if (!open) setDetailUser(null);
        }}
      >
        <SheetContent
          side="right"
          className="!max-w-2xl sm:!max-w-2xl flex flex-col p-0 gap-0"
        >
          {detailUser &&
            (() => {
              const rolesByOrg = new Map<
                string,
                {
                  org: { id: string; name: string; shortName: string } | null;
                  grants: UserRole[];
                }
              >();
              detailUser.platformRoles?.forEach((r) => {
                const key = r.scopeOrganization?.id ?? "__global__";
                if (!rolesByOrg.has(key))
                  rolesByOrg.set(key, { org: r.scopeOrganization, grants: [] });
                rolesByOrg.get(key)!.grants.push(r);
              });

              return (
                <>
                  <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <div className="flex items-start gap-3">
                      <div
                        className={`size-12 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                          detailUser.isActive
                            ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300"
                            : "bg-muted d"
                        }`}
                      >
                        {detailUser.displayName
                          ?.split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <SheetTitle className="truncate text-base">
                          {detailUser.displayName}
                        </SheetTitle>
                        <SheetDescription className="flex items-center gap-1.5 truncate mt-0.5">
                          <Mail className="size-3 flex-shrink-0" />
                          {detailUser.email}
                        </SheetDescription>
                        <div className="mt-2">
                          <StatusBadge
                            status={detailUser.isActive ? "active" : "inactive"}
                            label={detailUser.isActive ? "Active" : "Inactive"}
                          />
                        </div>
                      </div>
                    </div>
                  </SheetHeader>

                  {/* Tabs */}
                  <div className="border-b border-border px-6">
                    <div className="flex items-center gap-1">
                      {[
                        { key: "overview" as const, label: "Overview" },
                        {
                          key: "roles" as const,
                          label: `Roles & Orgs (${detailUser.platformRoles?.length ?? 0})`,
                        },
                      ].map((t) => {
                        const isActive = drawerTab === t.key;
                        return (
                          <button
                            key={t.key}
                            onClick={() => setDrawerTab(t.key)}
                            className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                              isActive
                                ? "border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400"
                                : "border-transparent d hover:text-foreground hover:border-border"
                            }`}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tab content */}
                  <div className="flex-1 overflow-auto p-6">
                    {drawerTab === "overview" && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4 px-4 py-3 rounded-lg bg-muted/30 dark:bg-white/[0.02]">
                          <div>
                            <p className="text-[10px] font-bold d tracking-wider mb-0.5">
                              Last Login
                            </p>
                            <p className="text-sm">
                              {detailUser.lastLoginAt
                                ? formatRelative(detailUser.lastLoginAt)
                                : "Never"}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold d tracking-wider mb-0.5">
                              Member Since
                            </p>
                            <p className="text-sm">
                              {detailUser.createdAt
                                ? new Date(
                                    detailUser.createdAt,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : "—"}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold d tracking-wider mb-2">
                            Organizations
                          </p>
                          {detailUser.organizations?.length ? (
                            <div className="flex flex-wrap gap-1.5">
                              {detailUser.organizations.map((o) => (
                                <span
                                  key={o.id}
                                  className="text-[11px] px-2 py-1 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 font-medium border border-amber-200/40 dark:border-amber-500/15"
                                  title={o.name}
                                >
                                  {o.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm d/50">—</p>
                          )}
                        </div>
                      </div>
                    )}

                    {drawerTab === "roles" &&
                      (rolesByOrg.size === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <CheckCircle2 className="size-8 d/30 mb-3" />
                          <p className="text-sm d">No roles assigned</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-xs d">
                            Role assignments are managed through AD group
                            mappings on the <strong>Roles</strong> page. To
                            change what this user has access to, modify the
                            relevant mapping or the user's group membership.
                          </p>
                          <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
                            {[...rolesByOrg.entries()].map(
                              ([key, { org, grants }]) => (
                                <div
                                  key={key}
                                  className="px-4 py-3 space-y-2.5"
                                >
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="inline-block text-[11px] px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 font-bold border border-amber-200/40 dark:border-amber-500/15 flex-shrink-0"
                                      title={org?.name ?? "All Organizations"}
                                    >
                                      {org?.shortName ?? "Global"}
                                    </span>
                                    <span className="text-xs d truncate">
                                      {org?.name ?? "All Organizations"}
                                    </span>
                                  </div>
                                  <div className="space-y-1.5 pl-1">
                                    {grants.map((g) => {
                                      const candidates = provenanceForGrant(g);
                                      return (
                                        <div
                                          key={g.id}
                                          className="flex items-center gap-2 flex-wrap"
                                        >
                                          <span className="text-[11px] px-2 py-0.5 rounded bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 font-medium ring-1 ring-violet-200/60 dark:ring-violet-500/20">
                                            {g.displayName}
                                          </span>
                                          <span className="text-[10px] d">
                                            via
                                          </span>
                                          {candidates.length === 0 ? (
                                            <span className="text-[10px] d italic">
                                              unknown mapping
                                            </span>
                                          ) : candidates.length === 1 ? (
                                            <span
                                              className="text-[10px] px-2 py-0.5 rounded bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400 font-medium font-mono truncate max-w-[200px]"
                                              title={candidates[0].groupDn}
                                            >
                                              {shortGroupName(
                                                candidates[0].groupName,
                                              )}
                                            </span>
                                          ) : (
                                            <span
                                              className="text-[10px] px-2 py-0.5 rounded bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400 font-medium"
                                              title={candidates
                                                .map((c) =>
                                                  shortGroupName(c.groupName),
                                                )
                                                .join("\n")}
                                            >
                                              {candidates.length} mapped groups
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              );
            })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ── Helpers ── */

function shortGroupName(dnOrName: string): string {
  return dnOrName.split(",")[0].replace(/^[Cc][Nn]=/, "");
}

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}
